import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Button from '@welldev-group/rcl-button';
import Input from '@welldev-group/rcl-input';
import SelectBox from '@welldev-group/rcl-select-box';
import { Textarea } from '@welldev-group/react-comp-lib';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { GET_PLACE } from '../../graphql/queries/place';
import { GET_COUNTRIES } from '../../graphql/queries/country';
import { CREATE_PLACE, EDIT_PLACE } from '../../graphql/mutations/place';
import { useTranslation } from 'react-i18next';
import imageUrl from '../../helpers/imageUrl';
import clearCache from '../../helpers/clearCache';
import FileUpload from '../common/FileUpload';
import ErrorComponent from '../common/ErrorComponent';
import WithErrorHandler from '../common/WithErrorHandler';
import { getPlace } from '../../graphql/queries/place';
import returnEmptyStringWhenNull from '../../helpers/returnEmptyStringWhenNull';
import { debounce } from 'lodash';

const PlaceForm = props => {
  const { t } = useTranslation();
  let { id } = useParams();
  const isNewRecord = !id;

  const [createPlace, callbackDataOnCreate] = useMutation(CREATE_PLACE, {
    update: cache => clearCache(cache, /Place/),
  });
  const [editPlace, callbackDataOnEdit] = useMutation(EDIT_PLACE);
  const { loading: countriesLoading, data: countriesData } = useQuery(GET_COUNTRIES);
  const placeFromId = useQuery(GET_PLACE, {
    variables: { id },
    skip: isNewRecord,
  });

  const [name, setName] = useState('');
  const [countryId, setCountryId] = useState('');
  const [description, setDescription] = useState('');
  const [publishDescription, setPublishDescription] = useState(false);
  const [twitterHashtag, setTwitterHashtag] = useState('');

  const [geonameIdentifier, setGeonameIdentifier] = useState(null);
  const [admin1Code, setAdmin1Code] = useState('');
  const [admin2Code, setAdmin2Code] = useState('');
  const [admin3Code, setAdmin3Code] = useState('');
  const [admin4Code, setAdmin4Code] = useState('');

  const [featureCode, setFeatureCode] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [timezone, setTimezone] = useState('');

  const [image, setImage] = useState(null);
  const [previousImage, setPreviousImage] = useState(null);

  const onNameChange = value => {
    setName(value);
  };

  const onDescriptionChange = event => {
    setDescription(event.target.value);
  };

  const onPublishDescriptionChange = selectedOption => {
    setPublishDescription(selectedOption.value);
  };

  const onTwitterHashtagChange = value => {
    setTwitterHashtag(value);
  };

  const onGeonameIdentifierChange = value => {
    setGeonameIdentifier(value);
  };

  const onAdmin1CodeChange = value => {
    setAdmin1Code(value);
  };

  const onAdmin2CodeChange = value => {
    setAdmin2Code(value);
  };

  const onAdmin3CodeChange = value => {
    setAdmin3Code(value);
  };

  const onAdmin4CodeChange = value => {
    setAdmin4Code(value);
  };

  const onFeatureCodeChange = value => {
    setFeatureCode(value);
  };

  const onLatitudeChange = value => {
    setLatitude(value);
  };

  const onLongitudeChange = value => {
    setLongitude(value);
  };

  const onTimezoneChange = value => {
    setTimezone(value);
  };

  const onCountryIdChange = selectedOption => {
    setCountryId(selectedOption.value);
  };

  const onImageFileUpload = files => {
    setImage(files[0]);
  };

  useEffect(() => {
    if (placeFromId && placeFromId.data && !placeFromId.loading) {
      setName(placeFromId.data.place.name);
      setCountryId(placeFromId.data.place.country.id);
      setDescription(returnEmptyStringWhenNull(placeFromId.data.place.description));
      setPublishDescription(placeFromId.data.place.publishDescription);
      setTwitterHashtag(returnEmptyStringWhenNull(placeFromId.data.place.twitterHashtag));
      setGeonameIdentifier(returnEmptyStringWhenNull(placeFromId.data.place.geonameIdentifier));
      setAdmin1Code(returnEmptyStringWhenNull(placeFromId.data.place.admin1Code));
      setAdmin2Code(returnEmptyStringWhenNull(placeFromId.data.place.admin2Code));
      setAdmin3Code(returnEmptyStringWhenNull(placeFromId.data.place.admin3Code));
      setAdmin4Code(returnEmptyStringWhenNull(placeFromId.data.place.admin4Code));
      setFeatureCode(returnEmptyStringWhenNull(placeFromId.data.place.featureCode));
      setLatitude(placeFromId.data.place.latitude);
      setLongitude(placeFromId.data.place.longitude);
      setTimezone(placeFromId.data.place.timezone);
      setPreviousImage(placeFromId.data.place.imageUrls.large);
    }
  }, [placeFromId]);

  const submitToBackEnd = debounce(() => {
    props.resetError();

    if (isNewRecord) {
      createPlace({
        variables: {
          name,
          countryId,
          description,
          publishDescription,
          twitterHashtag,
          geonameIdentifier,
          admin1Code,
          admin2Code,
          admin3Code,
          admin4Code,
          featureCode,
          latitude,
          longitude,
          timezone,
          image,
        },
      })
        .then(({ data }) => {
          props.history.push(`/admin/places/${data.createPlace.id}`);
        })
        .catch(() => props.onError());
    } else {
      editPlace({
        variables: {
          id,
          name,
          countryId,
          description,
          publishDescription,
          twitterHashtag,
          geonameIdentifier,
          admin1Code,
          admin2Code,
          admin3Code,
          admin4Code,
          featureCode,
          latitude,
          longitude,
          timezone,
          image,
        },
        refetchQueries: [getPlace(id)],
      })
        .then(({ data }) => {
          props.history.push(`/admin/places/${data.editPlace.id}`);
        })
        .catch(() => props.onError());
    }
  }, 500);

  const publishDescriptionOptions = [
    { value: true, label: t('shared.true') },
    { value: false, label: t('shared.false') },
  ];

  let countryOptions;

  if (countriesLoading) {
    countryOptions = [];
  } else {
    countryOptions = countriesData.countries.edges.map(country => ({
      value: country.node.id,
      label: country.node.name,
    }));
  }

  return (
    <div className="common-form">
      <div className="common-form__title">
        {isNewRecord ?
          t('shared.new', { entityName: t('places.show.title') })
          : t('shared.editTitle', { entityName: t('places.show.title') })}
      </div>
      <div className="common-form__error-message">
        {props.hasError && (
          <div className="col-6">
            <ErrorComponent error={{ callbackDataOnEdit, callbackDataOnCreate }} />
          </div>
        )}
      </div>
      <form className="common-form__container">
        <div className="grid">
          <div className="col-lg-6 col-md-10 col-sm-10 col-xs-10">
            <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12">
              <div className="common-form__field place-form__name">
                <Input
                    label={t('places.attributes.name')}
                    value={name}
                    onChange={onNameChange}
                    isClearable={false}
                />
              </div>
              <div className="common-form__field place-form__twitterHashtag">
                <Input
                  label={t('places.attributes.twitterHashtag')}
                  value={twitterHashtag}
                  onChange={onTwitterHashtagChange}
                  isClearable={false}
                />
              </div>
              <div className="common-form__field place-form__geonameIdentifier">
                <Input
                  type="number"
                  label={t('places.attributes.geonameIdentifier')}
                  value={geonameIdentifier}
                  onChange={onGeonameIdentifierChange}
                  isClearable={false}
                />
              </div>
              <div className="common-form__field place-form__admin1Code">
                <Input
                  label={t('places.attributes.admin1Code')}
                  value={admin1Code}
                  onChange={onAdmin1CodeChange}
                  isClearable={false}
                />
              </div>
              <div className="common-form__field place-form__admin2Code">
                <Input
                  label={t('places.attributes.admin2Code')}
                  value={admin2Code}
                  onChange={onAdmin2CodeChange}
                  isClearable={false}
                />
              </div>
              <div className="common-form__field place-form__admin3Code">
                <Input
                  label={t('places.attributes.admin3Code')}
                  value={admin3Code}
                  onChange={onAdmin3CodeChange}
                  isClearable={false}
                />
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-6 col-xs-12">
              <div className="common-form__field place-form__country">
                <SelectBox
                  label={t('places.attributes.country')}
                  value={countryOptions.find(option => option.value === countryId)}
                  options={countryOptions}
                  onChange={onCountryIdChange}
                  isClearable={false}
                />
              </div>

              <div className="common-form__field place-form__admin4Code">
                <Input
                  label={t('places.attributes.admin4Code')}
                  value={admin4Code}
                  onChange={onAdmin4CodeChange}
                  isClearable={false}
                />
              </div>
              <div className="common-form__field place-form__featureCode">
                <Input
                  label={t('places.attributes.featureCode')}
                  value={featureCode}
                  onChange={onFeatureCodeChange}
                  isClearable={false}
                />
              </div>
              <div className="common-form__field place-form__latitude">
                <Input
                  type="number"
                  label={t('places.attributes.latitude')}
                  value={latitude}
                  onChange={onLatitudeChange}
                  isClearable={false}
                />
              </div>
              <div className="common-form__field place-form__longitude">
                <Input
                  type="number"
                  label={t('places.attributes.longitude')}
                  value={longitude}
                  onChange={onLongitudeChange}
                  isClearable={false}
                />
              </div>
              <div className="common-form__field place-form__timezone">
                <Input
                  label={t('places.attributes.timezone')}
                  value={timezone}
                  onChange={onTimezoneChange}
                  isClearable={false}
                />
              </div>
            </div>
            <div className="col-12">
              <div className="common-form__field place-form__description">
                <Textarea
                  label={t('places.attributes.description')}
                  value={description}
                  onChange={onDescriptionChange}
                  placeholder={''}
                />
              </div>
              <div className="common-form__field place-form__publish-description">
                <SelectBox
                  label={t('places.attributes.publishDescription')}
                  value={publishDescriptionOptions.find(option => option.value === publishDescription)}
                  options={publishDescriptionOptions}
                  onChange={onPublishDescriptionChange}
                  isClearable={false}
                />
              </div>
            </div>
            <div className="common-form__submit-button">
              <Button
                label={isNewRecord ? t('shared.create') : t('shared.update')}
                onClick={submitToBackEnd}
              />
            </div>
          </div>
          <div className="col-6">
            <h2>{t('places.attributes.image')}</h2>
            {previousImage && (
              <div className="common-form__previous-image">
                <img src={imageUrl(previousImage)} alt="preview" />
              </div>
            )}
            <FileUpload onFileUpload={onImageFileUpload} multiple={false} />
          </div>
        </div>
      </form>
    </div>
  );
};

export default WithErrorHandler(PlaceForm);
